import {
  annotation as csAnnotation,
  utilities as csToolUtils,
  cursors as csCursors,
  drawing as csDrawing,
  Enums as ToolEnums,
  state as csToolsState,
} from '@cornerstonejs/tools';
import {
  getEnabledElement,
  metaData,
  eventTarget,
  triggerEvent,
} from '@cornerstonejs/core';
import type { Types as CoreTypes } from '@cornerstonejs/core';
import {
  SPECTRAL_DOPPLER_OUT_OF_REGION,
  SPECTRAL_DOPPLER_TOOL_NAMES,
} from '@/models/spectralDoppler';
import {
  resolveSpectralRegion,
  buildCalibrationFromRegion,
} from './calibrationRegion';
import {
  deriveSegmentValues,
  isPointInRegionBbox,
  clampToRegionBbox,
} from './calibrationMath';
import {
  formatDopplerTextLines,
  markSpectralAnnotationData,
  SpectralDopplerBaseTool,
  type UltrasoundRegionsMeta,
  type SpectralDopplerAnnotationData,
} from './SpectralDopplerBaseTool';
import { worldToPixel, clampWorldToRegionBbox } from './worldPixelCoords';
import { distanceToSegment } from './geometry';
import { applySmartLabelAnchor } from '../labelPlacement';

const {
  state: annotationStateApi,
  locking: annotationLocking,
  visibility: annotationVisibility,
} = csAnnotation;
const { viewportFilters } = csToolUtils;
const { elementCursor } = csCursors;

interface SegmentAnnotationData extends SpectralDopplerAnnotationData {
  handles: {
    points: CoreTypes.Point3[];
    activeHandleIndex: number | null;
    textBox?: unknown;
  };
  cachedStats: Record<string, unknown>;
  label: string;
}

interface SegmentAnnotation {
  annotationUID: string;
  highlighted: boolean;
  invalidated: boolean;
  isLocked: boolean;
  isVisible: boolean;
  metadata: { toolName: string };
  data: SegmentAnnotationData;
}

function defaultGetTextLines(data: SegmentAnnotationData): string[] {
  if (!data.spectralValues) return [];
  return formatDopplerTextLines('segment', data.spectralValues);
}

class VelocitySegmentTool extends SpectralDopplerBaseTool {
  static toolName = SPECTRAL_DOPPLER_TOOL_NAMES.SEGMENT;

  constructor(
    toolProps: Record<string, unknown> = {},
    defaultToolProps: Record<string, unknown> = {
      supportedInteractionTypes: ['Mouse', 'Touch'],
      configuration: {
        getTextLines: defaultGetTextLines,
      },
    }
  ) {
    super(toolProps, defaultToolProps);

    const readCalibration = (
      viewport: CoreTypes.IViewport
    ): ReturnType<typeof buildCalibrationFromRegion> | null => {
      const imageId =
        (
          viewport as unknown as {
            getCurrentImageId?: () => string | undefined;
          }
        ).getCurrentImageId?.() ?? '';
      const regionsMeta = metaData.get('ultrasoundRegions', imageId) as
        | UltrasoundRegionsMeta
        | undefined;
      const { region } = resolveSpectralRegion(regionsMeta?.regions);
      if (!region) return null;
      return buildCalibrationFromRegion(region);
    };

    this.addNewAnnotation = (evt: {
      detail: {
        element: HTMLDivElement;
        currentPoints: { world: CoreTypes.Point3 };
      };
    }): SegmentAnnotation | undefined => {
      const { element, currentPoints } = evt.detail;
      const worldPos = currentPoints.world;

      const enabled = getEnabledElement(element);
      if (!enabled) return undefined;
      const { viewport } = enabled;

      const calibration = readCalibration(viewport);
      if (!calibration) return undefined;

      const [px, py] = worldToPixel(viewport, worldPos);
      if (!isPointInRegionBbox(px, py, calibration)) {
        triggerEvent(eventTarget, SPECTRAL_DOPPLER_OUT_OF_REGION, {
          viewportId: viewport.id,
          px,
          py,
        });
        return undefined;
      }

      elementCursor.hideElementCursor(element);
      this.isDrawing = true;

      const annotation = (
        this as unknown as {
          createAnnotation: (
            evt: unknown,
            points: CoreTypes.Point3[]
          ) => SegmentAnnotation;
        }
      ).createAnnotation(evt, [
        [...worldPos] as CoreTypes.Point3,
        [...worldPos] as CoreTypes.Point3,
      ]);

      annotation.data.spectralCalibration = calibration;
      annotation.data.spectralToolVariant = 'segment';
      annotation.data.handles.activeHandleIndex = 1;

      annotationStateApi.addAnnotation(annotation as unknown as never, element);

      const viewportIdsToRender =
        viewportFilters.getViewportIdsWithToolToRender(
          element,
          this.getToolName()
        );

      this.editData = {
        annotation,
        viewportIdsToRender,
        handleIndex: 1,
        newAnnotation: true,
        hasMoved: false,
      };

      this._activateDraw(element);
      (evt as { preventDefault?: () => void }).preventDefault?.();
      csToolUtils.triggerAnnotationRenderForViewportIds(viewportIdsToRender);
      return annotation;
    };

    this._dragCallback = (evt: {
      detail: {
        element: HTMLDivElement;
        currentPoints: { world: CoreTypes.Point3 };
        deltaPoints?: { world: CoreTypes.Point3 };
      };
    }) => {
      if (!this.editData) return;
      this.isDrawing = true;
      const { annotation, handleIndex, movingTextBox, newAnnotation } =
        this.editData;
      const { data } = annotation as SegmentAnnotation;

      (
        this as unknown as {
          createMemo: (
            element: HTMLDivElement,
            annotation: unknown,
            opts?: { newAnnotation?: boolean }
          ) => void;
        }
      ).createMemo(evt.detail.element, annotation, { newAnnotation });

      if (movingTextBox) {
        const delta = evt.detail.deltaPoints?.world;
        if (delta) {
          const tb = (
            data.handles as {
              textBox?: { worldPosition: number[]; userMoved?: boolean };
            }
          ).textBox;
          if (tb) {
            tb.worldPosition[0] += delta[0];
            tb.worldPosition[1] += delta[1];
            tb.worldPosition[2] += delta[2];
            tb.userMoved = true;
          }
        }
      } else if (handleIndex !== undefined) {
        const { world } = evt.detail.currentPoints;
        const enabled = getEnabledElement(evt.detail.element);
        const cal = data.spectralCalibration;
        const clampedWorld =
          enabled && cal
            ? clampWorldToRegionBbox(enabled.viewport, world, cal)
            : ([...world] as CoreTypes.Point3);
        data.handles.points[handleIndex] = clampedWorld;
        (annotation as { invalidated: boolean }).invalidated = true;
      }

      this.editData.hasMoved = true;
      csToolUtils.triggerAnnotationRenderForViewportIds(
        this.editData.viewportIdsToRender
      );
    };

    this._endCallback = (evt: { detail: { element: HTMLDivElement } }) => {
      const { element } = evt.detail;
      if (!this.editData) return;

      const { annotation, viewportIdsToRender, newAnnotation, hasMoved } =
        this.editData;

      if (newAnnotation && !hasMoved) {
        // Abandoned click — no drag happened. Remove and bail.
        const uid = (annotation as SegmentAnnotation).annotationUID;
        if (uid) annotationStateApi.removeAnnotation(uid);
        this._deactivateModify(element);
        this._deactivateDraw(element);
        elementCursor.resetElementCursor(element);
        this.editData = null;
        this.isDrawing = false;
        return;
      }

      const seg = annotation as SegmentAnnotation;
      seg.data.handles.activeHandleIndex = null;

      // Finalize: snapshot calibration + derive values
      const enabled = getEnabledElement(element);
      const viewport = enabled?.viewport;
      if (viewport) {
        const calibration =
          seg.data.spectralCalibration ?? readCalibration(viewport);
        if (calibration) {
          const [p1x, p1y] = worldToPixel(viewport, seg.data.handles.points[0]);
          const [p2x, p2y] = worldToPixel(viewport, seg.data.handles.points[1]);
          const [cp1x, cp1y] = clampToRegionBbox(p1x, p1y, calibration);
          const [cp2x, cp2y] = clampToRegionBbox(p2x, p2y, calibration);
          const values = deriveSegmentValues(
            [cp1x, cp1y],
            [cp2x, cp2y],
            calibration
          );
          markSpectralAnnotationData(
            seg.data as unknown as Record<string, unknown>,
            'segment',
            calibration,
            values,
            [
              [cp1x, cp1y],
              [cp2x, cp2y],
            ]
          );
        }
      }

      this._deactivateModify(element);
      this._deactivateDraw(element);
      elementCursor.resetElementCursor(element);

      csToolUtils.triggerAnnotationRenderForViewportIds(viewportIdsToRender);
      if (newAnnotation) {
        annotationStateApi.triggerAnnotationCompleted(
          annotation as unknown as never
        );
      }

      (this as unknown as { doneEditMemo?: () => void }).doneEditMemo?.();

      this.editData = null;
      this.isDrawing = false;
    };

    this.cancel = (element: HTMLDivElement): string | undefined => {
      if (!this.isDrawing || !this.editData) return undefined;
      const { annotation } = this.editData;
      this.isDrawing = false;
      this._deactivateModify(element);
      this._deactivateDraw(element);
      elementCursor.resetElementCursor(element);
      const uid = (annotation as SegmentAnnotation).annotationUID;
      if (uid) annotationStateApi.removeAnnotation(uid);
      this.editData = null;
      return uid;
    };

    this._activateDraw = (element: HTMLDivElement) => {
      csToolsState.isInteractingWithTool = true;
      element.addEventListener(
        ToolEnums.Events.MOUSE_UP,
        this._endCallback as EventListener
      );
      element.addEventListener(
        ToolEnums.Events.MOUSE_DRAG,
        this._dragCallback as EventListener
      );
      element.addEventListener(
        ToolEnums.Events.MOUSE_MOVE,
        this._dragCallback as EventListener
      );
      element.addEventListener(
        ToolEnums.Events.MOUSE_CLICK,
        this._endCallback as EventListener
      );
      element.addEventListener(
        ToolEnums.Events.TOUCH_END,
        this._endCallback as EventListener
      );
      element.addEventListener(
        ToolEnums.Events.TOUCH_DRAG,
        this._dragCallback as EventListener
      );
      element.addEventListener(
        ToolEnums.Events.TOUCH_TAP,
        this._endCallback as EventListener
      );
    };

    this._deactivateDraw = (element: HTMLDivElement) => {
      csToolsState.isInteractingWithTool = false;
      element.removeEventListener(
        ToolEnums.Events.MOUSE_UP,
        this._endCallback as EventListener
      );
      element.removeEventListener(
        ToolEnums.Events.MOUSE_DRAG,
        this._dragCallback as EventListener
      );
      element.removeEventListener(
        ToolEnums.Events.MOUSE_MOVE,
        this._dragCallback as EventListener
      );
      element.removeEventListener(
        ToolEnums.Events.MOUSE_CLICK,
        this._endCallback as EventListener
      );
      element.removeEventListener(
        ToolEnums.Events.TOUCH_END,
        this._endCallback as EventListener
      );
      element.removeEventListener(
        ToolEnums.Events.TOUCH_DRAG,
        this._dragCallback as EventListener
      );
      element.removeEventListener(
        ToolEnums.Events.TOUCH_TAP,
        this._endCallback as EventListener
      );
    };

    this._activateModify = (element: HTMLDivElement) => {
      csToolsState.isInteractingWithTool = true;
      element.addEventListener(
        ToolEnums.Events.MOUSE_UP,
        this._endCallback as EventListener
      );
      element.addEventListener(
        ToolEnums.Events.MOUSE_DRAG,
        this._dragCallback as EventListener
      );
      element.addEventListener(
        ToolEnums.Events.MOUSE_CLICK,
        this._endCallback as EventListener
      );
      element.addEventListener(
        ToolEnums.Events.TOUCH_END,
        this._endCallback as EventListener
      );
      element.addEventListener(
        ToolEnums.Events.TOUCH_DRAG,
        this._dragCallback as EventListener
      );
      element.addEventListener(
        ToolEnums.Events.TOUCH_TAP,
        this._endCallback as EventListener
      );
    };

    this._deactivateModify = (element: HTMLDivElement) => {
      csToolsState.isInteractingWithTool = false;
      element.removeEventListener(
        ToolEnums.Events.MOUSE_UP,
        this._endCallback as EventListener
      );
      element.removeEventListener(
        ToolEnums.Events.MOUSE_DRAG,
        this._dragCallback as EventListener
      );
      element.removeEventListener(
        ToolEnums.Events.MOUSE_CLICK,
        this._endCallback as EventListener
      );
      element.removeEventListener(
        ToolEnums.Events.TOUCH_END,
        this._endCallback as EventListener
      );
      element.removeEventListener(
        ToolEnums.Events.TOUCH_DRAG,
        this._dragCallback as EventListener
      );
      element.removeEventListener(
        ToolEnums.Events.TOUCH_TAP,
        this._endCallback as EventListener
      );
    };

    this.handleSelectedCallback = (
      evt: { detail: { element: HTMLDivElement } },
      annotation: SegmentAnnotation,
      handle: unknown
    ) => {
      const { element } = evt.detail;
      // eslint-disable-next-line no-param-reassign
      annotation.highlighted = true;
      let movingTextBox = false;
      let handleIndex: number | undefined;
      const h = handle as { worldPosition?: unknown };
      if (h.worldPosition) {
        movingTextBox = true;
      } else {
        handleIndex = annotation.data.handles.points.findIndex(
          (p) => p === handle
        );
        if (handleIndex === -1) handleIndex = undefined;
      }
      const viewportIdsToRender =
        viewportFilters.getViewportIdsWithToolToRender(
          element,
          this.getToolName()
        );
      this.editData = {
        annotation,
        viewportIdsToRender,
        handleIndex,
        movingTextBox,
      };
      this._activateModify(element);
      elementCursor.hideElementCursor(element);
      csToolUtils.triggerAnnotationRenderForViewportIds(viewportIdsToRender);
      (evt as unknown as { preventDefault?: () => void }).preventDefault?.();
    };

    this.toolSelectedCallback = (
      evt: { detail: { element: HTMLDivElement } },
      annotation: SegmentAnnotation
    ) => {
      const { element } = evt.detail;
      // eslint-disable-next-line no-param-reassign
      annotation.highlighted = true;
      const viewportIdsToRender =
        viewportFilters.getViewportIdsWithToolToRender(
          element,
          this.getToolName()
        );
      this.editData = {
        annotation,
        viewportIdsToRender,
        movingTextBox: false,
      };
      this._activateModify(element);
      elementCursor.hideElementCursor(element);
      csToolUtils.triggerAnnotationRenderForViewportIds(viewportIdsToRender);
    };

    this.isPointNearTool = (
      element: HTMLDivElement,
      annotation: SegmentAnnotation,
      canvasCoords: CoreTypes.Point2,
      proximity: number
    ): boolean => {
      const enabled = getEnabledElement(element);
      if (!enabled) return false;
      const { viewport } = enabled;
      const [p1, p2] = annotation.data.handles.points;
      if (!p1 || !p2) return false;
      const c1 = viewport.worldToCanvas(p1);
      const c2 = viewport.worldToCanvas(p2);
      return distanceToSegment(c1, c2, canvasCoords) <= proximity;
    };

    this.renderAnnotation = (
      enabledElement: CoreTypes.IEnabledElement,
      svgDrawingHelper: unknown
    ): boolean => {
      let rendered = false;
      const { viewport } = enabledElement;
      const { element } = viewport;

      let annotations = annotationStateApi.getAnnotations(
        this.getToolName(),
        element
      ) as unknown as SegmentAnnotation[] | undefined;
      if (!annotations?.length) return rendered;
      annotations = this.filterInteractableAnnotationsForElement(
        element,
        annotations as unknown as Record<string, unknown>[]
      ) as unknown as SegmentAnnotation[];
      if (!annotations?.length) return rendered;

      const styleSpecifier = {
        toolGroupId: this.toolGroupId,
        toolName: this.getToolName(),
        viewportId: viewport.id,
      };

      for (let i = 0; i < annotations.length; i += 1) {
        const annotation = annotations[i];
        const { annotationUID, data } = annotation;
        const { points } = data.handles;
        if (
          points.length >= 2 &&
          annotationVisibility.isAnnotationVisible(annotationUID)
        ) {
          // Recompute on invalidation (handle drag). Handle worlds are clamped
          // to the bbox at drag time, so derived pixels are already inside.
          if (annotation.invalidated && data.spectralCalibration) {
            const cal = data.spectralCalibration;
            const [p1x, p1y] = worldToPixel(viewport, points[0]);
            const [p2x, p2y] = worldToPixel(viewport, points[1]);
            data.spectralValues = deriveSegmentValues(
              [p1x, p1y],
              [p2x, p2y],
              cal
            );
            data.spectralPixelPoints = [
              [p1x, p1y],
              [p2x, p2y],
            ];
            annotation.invalidated = false;
            annotationStateApi.triggerAnnotationModified(
              annotation as unknown as never,
              element,
              ToolEnums.ChangeTypes.StatsUpdated
            );
          }

          const styleCtx = { ...styleSpecifier, annotationUID };
          const { color, lineWidth } = this.getAnnotationStyle({
            annotation,
            styleSpecifier: styleCtx,
          }) as { color: string; lineWidth: number };

          const c1 = viewport.worldToCanvas(points[0]);
          const c2 = viewport.worldToCanvas(points[1]);

          csDrawing.drawLine(
            svgDrawingHelper as never,
            annotationUID,
            `${annotationUID}-segment`,
            c1,
            c2,
            { color, width: lineWidth }
          );

          if (!annotationLocking.isAnnotationLocked(annotationUID)) {
            csDrawing.drawHandles(
              svgDrawingHelper as never,
              annotationUID,
              `${annotationUID}-handles`,
              [c1, c2],
              { color }
            );
          }

          const getTextLines = this.configuration?.getTextLines as
            | ((data: SegmentAnnotationData) => string[])
            | undefined;
          const textLines = getTextLines?.(data) ?? [];

          applySmartLabelAnchor({
            annotation: annotation as unknown as {
              data: { handles: { textBox?: unknown } };
            },
            geometryCanvasPoints: [c1, c2],
            spot: { point: c1, side: 'above', lockedAt: points[0] },
            lines: textLines,
            viewport: viewport as unknown as {
              element: HTMLElement;
              canvasToWorld: (p: CoreTypes.Point2) => CoreTypes.Point3;
            },
          });

          this.renderLinkedTextBoxAnnotation({
            enabledElement,
            svgDrawingHelper,
            annotation,
            styleSpecifier: styleCtx,
            textLines,
            canvasCoordinates: [c1, c2],
          });

          rendered = true;

          const flagged = annotation as unknown as {
            _spectralStatsEmitted?: boolean;
          };
          // Same guard as VTITraceTool: the bootstrap emission is for reloaded
          // HUMAN annotations only — an AI annotation's first render must not
          // trigger capture's instant-adopt path (unsaved-changes on load).
          if (
            !flagged._spectralStatsEmitted &&
            data.spectralValues &&
            !(data as { isAI?: boolean }).isAI
          ) {
            flagged._spectralStatsEmitted = true;
            annotationStateApi.triggerAnnotationModified(
              annotation as unknown as never,
              element,
              ToolEnums.ChangeTypes.StatsUpdated
            );
          }
        }
      }

      return rendered;
    };
  }
}

export default VelocitySegmentTool;
